import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { fetchCurrentUser } from '../modules/users';
import {
  buildPermissionSet,
  checkPermission,
  checkAnyPermission,
  checkAllPermissions,
} from '../lib/permissions';

const UserContext = createContext(null);

const normalizePermissions = (permissions = []) =>
  [...new Set(permissions.filter(Boolean))];

export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!localStorage.getItem('token')
  );

  const applyUserSession = useCallback((user, org = null, permissions = []) => {
    setCurrentUser(user);
    setRole(user?.role ?? null);
    setOrganization(org);
    setUserPermissions(
      normalizePermissions(
        permissions.length ? permissions : user?.permissions ?? []
      )
    );
  }, []);

  const refreshUser = useCallback(async () => {
    if (!localStorage.getItem('token')) {
      setHasLoaded(true);
      return null;
    }

    setIsLoading(true);
    try {
      const user = await fetchCurrentUser();
      if (user) {
        applyUserSession(user, user?.organization ?? null, user?.permissions ?? []);
      }
      setHasLoaded(true);
      return user;
    } catch (err) {
      setHasLoaded(true);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [applyUserSession]);

  useEffect(() => {
    if (!hasLoaded) {
      refreshUser().catch(console.error);
    }
  }, [hasLoaded, refreshUser]);

  const login = useCallback((data) => {
    if (data?.auth_token) {
      localStorage.setItem('token', `Bearer ${data.auth_token}`);
    }
    localStorage.setItem('isLoggedIn', 'true');
    setIsAuthenticated(true);
    setHasLoaded(false);
  }, []);

  const clearUserData = useCallback(() => {
    setCurrentUser(null);
    setRole(null);
    setOrganization(null);
    setUserPermissions([]);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('isLoggedIn');
    setHasLoaded(true);
  }, []);

  const updateUserData = useCallback((updatedUser) => {
    if (!updatedUser) return;
    setCurrentUser(updatedUser);
    setRole(updatedUser?.role ?? null);
    if (updatedUser?.permissions) {
      setUserPermissions(normalizePermissions(updatedUser.permissions));
    }
  }, []);

  const updateOrganizationData = useCallback((updatedOrg) => {
    setOrganization(updatedOrg);
  }, []);

  const permissionSet = useMemo(
    () => buildPermissionSet(userPermissions),
    [userPermissions]
  );

  const can = useCallback(
    (permission) => checkPermission(permissionSet, permission),
    [permissionSet]
  );

  const hasAny = useCallback(
    (permissions) => checkAnyPermission(permissionSet, permissions),
    [permissionSet]
  );

  const hasAll = useCallback(
    (permissions) => checkAllPermissions(permissionSet, permissions),
    [permissionSet]
  );

  const value = useMemo(() => ({
    currentUser,
    role,
    organization,
    userPermissions,
    permissionSet,
    can,
    hasAny,
    hasAll,
    isLoading,
    hasLoaded,
    isAuthenticated,
    isReady: hasLoaded && !isLoading,
    refreshUser,
    updateUserData,
    updateOrganizationData,
    clearUserData,
    login,
  }), [
    currentUser, role, organization, userPermissions, permissionSet,
    can, hasAny, hasAll, isLoading, hasLoaded, isAuthenticated,
    refreshUser, updateUserData, updateOrganizationData, clearUserData, login,
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error('useUser must be used within a UserProvider');
  return context;
};